
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UserProgressData } from '@/hooks/useUserProgress';

interface ProgressStatsProps {
  progressData: UserProgressData[];
}

const ProgressStats: React.FC<ProgressStatsProps> = ({ progressData }) => {
  const completedNodes = progressData.filter(p => p.status === 'completed');
  const inProgressNodes = progressData.filter(p => p.status === 'in_progress');

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="h-5 w-5 text-green-500">✓</span>
            Completed ({completedNodes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {completedNodes.length > 0 ? (
            <div className="space-y-2">
              {completedNodes.slice(0, 5).map((progress) => (
                <div key={progress.id} className="flex items-center justify-between p-2 rounded bg-muted/50">
                  <span className="text-sm font-medium">Node {progress.node_id.slice(0, 8)}...</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">Completed</Badge>
                    {progress.time_spent_seconds && (
                      <Badge variant="outline" className="text-xs">
                        {Math.floor(progress.time_spent_seconds / 60)}m
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
              {completedNodes.length > 5 && (
                <p className="text-sm text-black">
                  +{completedNodes.length - 5} more completed
                </p>
              )}
            </div>
          ) : (
            <p className="text-black">No completed content yet. Start exploring!</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="h-5 w-5 text-blue-500">📖</span>
            In Progress ({inProgressNodes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {inProgressNodes.length > 0 ? (
            <div className="space-y-2">
              {inProgressNodes.slice(0, 5).map((progress) => (
                <div key={progress.id} className="flex items-center justify-between p-2 rounded bg-muted/50">
                  <span className="text-sm font-medium">Node {progress.node_id.slice(0, 8)}...</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">In Progress</Badge>
                    {progress.time_spent_seconds && (
                      <Badge variant="outline" className="text-xs">
                        {Math.floor(progress.time_spent_seconds / 60)}m
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
              {inProgressNodes.length > 5 && (
                <p className="text-sm text-black">
                  +{inProgressNodes.length - 5} more in progress
                </p>
              )}
            </div>
          ) : (
            <p className="text-black">No content in progress. Continue your learning journey!</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProgressStats;
